import { type ISummaryBlockOverride, LEXICANON_SUMMARY_BLOCKS, type TSummaryBlockType } from "@lexicanon/shared"
import { msg } from "@lingui/core/macro"
import { useLingui } from "@lingui/react"
import { Center, Loader } from "@mantine/core"
import { notifications } from "@mantine/notifications"
import { useQueryClient } from "@tanstack/react-query"
import { useEffect, useMemo, useState } from "react"
import { useAuth } from "../../hooks/useAuth"
import { useCompanyMutation, useCompanyQuery } from "../../hooks/useCompanyQuery"
import { fetchSummaryLayout, saveSummaryLayout } from "../../lib/api"
import { queryKeys } from "../../lib/query-keys"
import type { ISummaryBlockItem, TBlockColumn } from "./interfaces"
import { SummaryLayoutPanelView } from "./SummaryLayoutPanelView"

const nameFor = (type: string): string => LEXICANON_SUMMARY_BLOCKS.find((b) => b.type === type)?.displayName ?? type

// The served layout arrives loosely typed (Eden widens `type`/`column` to
// `string` over the wire), so we re-validate each row against the registry
// before trusting it as a known block type.
const KNOWN_TYPES: Set<string> = new Set(LEXICANON_SUMMARY_BLOCKS.map((b) => b.type))
const isBlockType = (type: string): type is TSummaryBlockType => KNOWN_TYPES.has(type)

/** One row of the served (wire-typed) layout. */
interface ILayoutRow {
  type: string
  column: string
  order: number
  visible: boolean
}

/** Resolved layout → editable list: non-required blocks, main column first then
 *  side, each in its stored order. (Required blocks like the Player are chrome.) */
function buildItems(layout: ILayoutRow[]): ISummaryBlockItem[] {
  const inColumn = (col: TBlockColumn): ISummaryBlockItem[] =>
    layout
      .filter((b) => b.column === col)
      .sort((a, b) => a.order - b.order)
      .flatMap((b) =>
        isBlockType(b.type) ? [{ type: b.type, displayName: nameFor(b.type), column: col, visible: b.visible }] : [],
      )
  return [...inColumn("main"), ...inColumn("side")]
}

/** Editable list → config blocks: per-column order derived from list position. */
function buildConfigBlocks(items: ISummaryBlockItem[]): ISummaryBlockOverride[] {
  const forColumn = (col: TBlockColumn): ISummaryBlockOverride[] =>
    items
      .filter((i) => i.column === col)
      .map((i, idx) => ({ type: i.type, column: col, order: idx, visible: i.visible }))
  return [...forColumn("main"), ...forColumn("side")]
}

const REQUIRED_NAMES = LEXICANON_SUMMARY_BLOCKS.filter((b) => b.required).map((b) => b.displayName)

/**
 * Smart wrapper for the meeting-summary curate panel. Loads the resolved
 * per-company layout, holds the editable arrangement, and saves it back. The
 * visual surface lives in the dumb {@link SummaryLayoutPanelView}.
 */
export function SummaryLayoutPanel(): React.JSX.Element {
  const { i18n } = useLingui()
  const queryClient = useQueryClient()
  const { user } = useAuth()
  const orgId = user?.activeOrganizationId ?? null

  const { data, isLoading } = useCompanyQuery({
    key: (org) => queryKeys.summaryLayout(org),
    queryFn: (company) => fetchSummaryLayout(company),
  })

  const initialItems = useMemo(() => (data ? buildItems(data.layout) : []), [data])
  const [items, setItems] = useState<ISummaryBlockItem[] | null>(null)

  // Seed the editable state once the layout loads.
  useEffect(() => {
    if (data && items === null) setItems(buildItems(data.layout))
  }, [data, items])

  const dirty = items !== null && JSON.stringify(items) !== JSON.stringify(initialItems)

  const { mutate, isPending } = useCompanyMutation({
    mutationFn: (company) => saveSummaryLayout(company, buildConfigBlocks(items ?? [])),
    onSuccess: () => {
      notifications.show({ message: i18n._(msg`Summary layout saved.`), color: "success" })
      queryClient.invalidateQueries({ queryKey: queryKeys.summaryLayout(orgId) })
      // Re-seed from the just-saved state so `dirty` clears.
      if (items) setItems(items)
    },
    onError: () => notifications.show({ message: i18n._(msg`Couldn't save the layout. Try again.`), color: "danger" }),
  })

  if (isLoading || items === null) {
    return (
      <Center py="xl">
        <Loader />
      </Center>
    )
  }

  return (
    <SummaryLayoutPanelView
      items={items}
      requiredNames={REQUIRED_NAMES}
      saving={isPending}
      dirty={dirty}
      onItemsChange={setItems}
      onSave={() => mutate()}
      onReset={() => setItems(initialItems)}
    />
  )
}
